import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import type { FeedHealthGroup, FeedHealthResponse, FeedHealthState } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const CARD: React.CSSProperties = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "14px 18px" };
const LIGHT: Record<FeedHealthState, string> = { ok: "#1f8a5b", alarm: "#c0392b", degraded: "#9a7b1f" };
const LIGHT_LABEL: Record<FeedHealthState, string> = { ok: "Healthy", alarm: "In alarm", degraded: "No data" };

function Dot({ health, size = 10 }: { health: FeedHealthState; size?: number }) {
  return <span style={{ width: size, height: size, borderRadius: "50%", background: LIGHT[health], display: "inline-block", flex: "none" }} />;
}

function stateBadge(state: string): { bg: string; fg: string; label: string } {
  if (state === "ALARM") return { bg: "#fbe4e2", fg: "#b3261e", label: "ALARM" };
  if (state === "INSUFFICIENT_DATA") return { bg: "#f3efe2", fg: "#7a5f14", label: "NO DATA" };
  return { bg: "#e4f1ea", fg: "#1f6d47", label: "OK" };
}

function GroupCard({ group }: { group: FeedHealthGroup }) {
  const [open, setOpen] = useState(group.health !== "ok"); // expand unhealthy groups by default
  return (
    <div style={CARD}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", background: "none", border: 0, padding: 0, cursor: "pointer", textAlign: "left" }}
      >
        <Icon name={open ? "chevron-down" : "chevron-right"} size={15} color="#8a8e88" />
        <Dot health={group.health} />
        <span style={{ fontSize: 14, fontWeight: 600, color: "#1d241f", flex: 1 }}>{group.subsystem}</span>
        <span style={{ fontSize: 11.5, color: "#8a8e88" }}>
          {group.counts.alarm > 0 && <b style={{ color: "#b3261e" }}>{group.counts.alarm} in alarm · </b>}
          {group.counts.ok} ok
          {group.counts.insufficient > 0 && ` · ${group.counts.insufficient} no-data`}
        </span>
      </button>
      {open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {group.alarms.map((a) => {
            const b = stateBadge(a.state);
            return (
              <div key={a.name} style={{ borderTop: "1px solid #f0efe9", paddingTop: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: b.fg, background: b.bg, borderRadius: 4, padding: "2px 6px", flex: "none" }}>{b.label}</span>
                  <span style={{ fontSize: 12.5, fontFamily: "'Space Mono',monospace", color: "#1d241f", flex: 1, wordBreak: "break-word" }}>{a.name}</span>
                  {a.metric && <span style={{ fontSize: 11, color: "#8a8e88", flex: "none" }}>{a.metric}</span>}
                </div>
                {a.reason && <div style={{ fontSize: 11, color: "#8a8e88", marginTop: 3, marginLeft: 2 }}>{a.reason}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NotEnabled({ data }: { data: Extract<FeedHealthResponse, { configured: false }> }) {
  return (
    <div style={{ ...CARD, color: "#7a5f14", background: "#fbf6e9", boxShadow: "inset 0 0 0 1px #efe2c0" }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        {data.disabled ? "Feed health isn’t enabled yet" : "AWS credentials not set"}
      </div>
      <div style={{ fontSize: 12 }}>
        {data.disabled
          ? "Turn on the feed_health flag once the read-only AWS credentials are in the worker to see live Displet pipeline alarms."
          : "Add the read-only AWS credentials (CloudWatch access) to the worker to observe the Displet MLS pipeline."}
      </div>
    </div>
  );
}

export function FeedHealth() {
  const q = useQuery({
    queryKey: ["feed-health"],
    queryFn: api.feedHealth,
    staleTime: 30_000,
    // Only poll live alarms when actually configured; don't hammer the 503 path.
    refetchInterval: (query) => (query.state.data && "configured" in query.state.data && query.state.data.configured ? 60_000 : false),
  });

  if (q.isLoading) return <Loading />;
  const data = q.data;
  // On a polling screen a failed background refetch keeps last-known-good `data`
  // in cache while setting `q.error`. Render the cached view and surface the
  // refresh failure as a non-blocking banner; only fall back to a full error
  // when there is nothing good to show.
  if (!data) return q.error ? <ErrorNote error={q.error} /> : <Loading />;

  return (
    <Page>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Feed health</h1>
          <div style={{ fontSize: 12.5, color: "#8a8e88", marginTop: 2 }}>
            Displet MLS pipeline — read-only CloudWatch alarms{data.configured ? ` · ${data.region}` : ""}
          </div>
        </div>
        {data.configured && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, ...CARD, padding: "8px 14px" }}>
            <Dot health={data.overall} size={12} />
            <span style={{ fontSize: 13, fontWeight: 600, color: LIGHT[data.overall] }}>{LIGHT_LABEL[data.overall]}</span>
            <span style={{ fontSize: 12, color: "#8a8e88" }}>
              {data.inAlarm > 0 ? `${data.inAlarm} of ${data.total} in alarm` : `${data.total} checks`}
            </span>
          </div>
        )}
      </div>

      {q.error && (
        <div style={{ ...CARD, background: "#fbf6e9", boxShadow: "inset 0 0 0 1px #efe2c0", color: "#7a5f14", fontSize: 12 }}>
          Couldn’t refresh just now — showing the last-known-good status{q.dataUpdatedAt ? ` from ${new Date(q.dataUpdatedAt).toLocaleTimeString()}` : ""}.
        </div>
      )}

      {!data.configured ? (
        <NotEnabled data={data} />
      ) : data.groups.length === 0 ? (
        <div style={{ ...CARD, color: "#8a8e88", fontSize: 13 }}>No Displet alarms found.</div>
      ) : (
        data.groups.map((g) => <GroupCard key={g.subsystem} group={g} />)
      )}
    </Page>
  );
}
